/**
 * MaintenancePopup
 *
 * Shown to ALL visitors when maintenance mode is ON.
 * Admins / staff still see it but get a dismissible banner instead
 * of the full blocking popup (so they can work on the site).
 *
 * Features:
 *  - Professional overlay with animated warning icon + pulse rings
 *  - Animated shimmer top bar
 *  - Optional countdown timer (pulled from backend settings)
 *  - Admins see a dismissible top banner so they can still browse
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Clock, Mail, X, Wrench, Wifi, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API_URL from '../config/api';

function pad(n) { return String(n).padStart(2, '0'); }

function CountdownTimer({ timerEnd }) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!timerEnd) return;
    const calc = () => {
      const diff = new Date(timerEnd) - Date.now();
      if (diff <= 0) { setRemaining(null); return; }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setRemaining({ h, m, s });
    };
    calc();
    const id = setInterval(calc, 1_000);
    return () => clearInterval(id);
  }, [timerEnd]);

  if (!remaining) return null;

  return (
    <div className="mt-6">
      <p className="text-gray-500 text-xs text-center mb-3 flex items-center justify-center gap-1.5">
        <Clock size={12} className="text-amber-400" />
        Estimated back online in
      </p>
      <div className="flex items-center justify-center gap-2">
        {[
          { val: remaining.h, label: 'HRS' },
          { val: remaining.m, label: 'MIN' },
          { val: remaining.s, label: 'SEC' },
        ].map((unit, i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center">
              <motion.div
                key={unit.val}
                initial={{ y: -6, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="bg-[#0a0f1a] border border-amber-500/30 rounded-lg px-3 py-2 font-mono text-amber-400 text-xl font-bold min-w-[3rem] text-center"
                style={{ boxShadow: '0 0 12px rgba(245,158,11,0.15)' }}
              >
                {pad(unit.val)}
              </motion.div>
              <span className="text-[10px] text-gray-600 font-mono mt-1">{unit.label}</span>
            </div>
            {i < 2 && <span className="text-amber-500/50 font-mono text-xl mb-4">:</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function GridOverlay() {
  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl"
      style={{ opacity: 0.04 }}
    >
      <div
        className="w-full h-full"
        style={{
          backgroundImage:
            'linear-gradient(rgba(245,158,11,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.5) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
    </div>
  );
}

export default function MaintenancePopup() {
  const { user } = useAuth();
  const [settings, setSettings]   = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [bypassed,  setBypassed]  = useState(
    () => !!sessionStorage.getItem('maintenance_bypassed')
  );

  useEffect(() => {
    fetch(`${API_URL}/api/site/maintenance`)
      .then(r => r.json())
      .then(d => setSettings(d))
      .catch(() => {});
  }, []);

  if (!settings?.enabled) return null;
  if (bypassed) return null;

  const handleBypass = () => {
    sessionStorage.setItem('maintenance_bypassed', '1');
    setBypassed(true);
  };

  const isStaff = user && (user.role === 'admin' || user.role === 'staff');

  /* ── Staff: dismissible top banner ─────────────────────────────────────── */
  if (isStaff) {
    if (dismissed) return null;
    return (
      <motion.div
        initial={{ y: -64 }}
        animate={{ y: 0 }}
        exit={{ y: -64 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between px-5 py-2.5 text-sm"
        style={{
          background: 'linear-gradient(90deg,#78350f 0%,#92400e 50%,#78350f 100%)',
          borderBottom: '1px solid rgba(245,158,11,0.4)',
          boxShadow: '0 4px 24px rgba(245,158,11,0.15)',
        }}
      >
        <div className="flex items-center gap-3 text-amber-200">
          <motion.div
            animate={{ rotate: [0, -8, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}
          >
            <Wrench size={15} className="text-amber-400" />
          </motion.div>
          <span className="font-mono font-bold tracking-wider text-amber-300">MAINTENANCE MODE ACTIVE</span>
          <span className="hidden sm:inline text-amber-400/60 text-xs">— public visitors see the maintenance screen</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden md:flex items-center gap-1 text-xs text-amber-500/70">
            <Shield size={11} /> Staff bypass active
          </span>
          <button
            onClick={() => setDismissed(true)}
            className="text-amber-500 hover:text-white transition-colors p-1 rounded"
            aria-label="Dismiss banner"
          >
            <X size={15} />
          </button>
        </div>
      </motion.div>
    );
  }

  /* ── Visitors: full blocking overlay ───────────────────────────────────── */
  const title      = settings.title        || 'Scheduled Maintenance';
  const message    = settings.message      || 'We are currently performing scheduled maintenance to improve your experience.';
  const email      = settings.contact_email;
  const allowClose = settings.allow_close  !== false;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(120,53,15,0.15) 0%, rgba(0,0,0,0.92) 70%)',
          backdropFilter: 'blur(12px)',
        }}
        onClick={(e) => { if (allowClose && e.target === e.currentTarget) handleBypass(); }}
      >
        {/* Ambient orb */}
        <motion.div
          className="absolute pointer-events-none"
          animate={{ scale: [1, 1.08, 1], opacity: [0.12, 0.2, 0.12] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            width: 480,
            height: 480,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(245,158,11,0.18) 0%, transparent 70%)',
          }}
        />

        <motion.div
          initial={{ scale: 0.88, y: 32, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.92, y: 16, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26, delay: 0.05 }}
          className="relative rounded-2xl max-w-lg w-full overflow-hidden"
          style={{
            background: 'linear-gradient(150deg, #0d1424 0%, #0f1520 50%, #0a0f1a 100%)',
            border: '1px solid rgba(245,158,11,0.28)',
            boxShadow: '0 0 0 1px rgba(245,158,11,0.06), 0 0 80px rgba(245,158,11,0.12), 0 32px 64px rgba(0,0,0,0.7)',
          }}
        >
          <GridOverlay />

          {/* Top shimmer bar */}
          <div className="relative h-1 w-full overflow-hidden">
            <div className="h-full w-full" style={{ background: 'linear-gradient(90deg,#92400e,#f59e0b,#d97706,#f59e0b,#92400e)' }} />
            <motion.div
              className="absolute inset-y-0 w-1/3"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)' }}
              animate={{ x: ['-100%', '400%'] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'linear', repeatDelay: 1 }}
            />
          </div>

          {allowClose && (
            <button
              onClick={handleBypass}
              className="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-gray-600 hover:text-white hover:bg-white/10 transition-all"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          )}

          <div className="px-8 pt-8 pb-7 text-center">
            {/* Animated icon with pulse rings */}
            <div className="relative inline-flex items-center justify-center mb-6">
              <motion.div
                className="absolute rounded-full border border-amber-500/20"
                animate={{ scale: [1, 1.7], opacity: [0.5, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                style={{ width: 80, height: 80 }}
              />
              <motion.div
                className="absolute rounded-full border border-amber-500/12"
                animate={{ scale: [1, 2.2], opacity: [0.3, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeOut', delay: 0.4 }}
                style={{ width: 80, height: 80 }}
              />
              <motion.div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(217,119,6,0.08))',
                  border: '1px solid rgba(245,158,11,0.3)',
                  boxShadow: '0 0 24px rgba(245,158,11,0.2)',
                }}
                animate={{ rotate: [-4, 4, -4] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Wrench size={28} className="text-amber-400" />
              </motion.div>
            </div>

            {/* Blinking status badge */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <motion.div
                className="w-2 h-2 rounded-full bg-amber-500"
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.2, repeat: Infinity }}
              />
              <span className="text-amber-500/80 text-xs font-mono tracking-widest uppercase">System Maintenance</span>
            </div>

            <h2 className="text-2xl font-bold text-white mb-3 tracking-tight">{title}</h2>

            <p className="text-gray-400 text-sm leading-relaxed mb-5 max-w-sm mx-auto">{message}</p>

            {/* Warning box */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="rounded-xl p-4 mb-5 text-left"
              style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)' }}
            >
              <div className="flex gap-2.5">
                <AlertTriangle size={13} className="text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-red-300/80 text-xs leading-relaxed font-mono">
                  <strong className="text-red-400">Important:</strong> Any payments, form submissions or data entered
                  during this window <strong>will not be recorded or processed</strong>. Please wait until we are back online.
                </p>
              </div>
            </motion.div>

            {settings.timer_enabled && settings.timer_end && (
              <CountdownTimer timerEnd={settings.timer_end} />
            )}

            {email && (
              <div className="flex items-center justify-center gap-1.5 mt-5 text-xs text-gray-600">
                <Mail size={11} className="text-amber-500/60" />
                <span>Questions?</span>
                <a href={`mailto:${email}`} className="text-amber-500 hover:text-amber-300 transition-colors underline underline-offset-2">
                  {email}
                </a>
              </div>
            )}

            {allowClose && (
              <button
                onClick={handleBypass}
                className="mt-6 text-[11px] text-gray-700 hover:text-gray-400 transition-colors underline underline-offset-2"
              >
                I understand the risks — proceed anyway
              </button>
            )}
          </div>

          <div
            className="px-8 py-3 flex items-center justify-center gap-2 text-[11px] text-gray-700 font-mono"
            style={{ borderTop: '1px solid rgba(255,255,255,0.04)', background: 'rgba(0,0,0,0.25)' }}
          >
            <Wifi size={11} className="text-gray-600" />
            We apologise for the inconvenience and will be back shortly.
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
